import React from 'react';
import PropTypes from 'prop-types';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import AppLoading from 'components/App/Loading';
import Breadcrumb from 'components/App/Breadcrumb';
import Card from 'components/App/Card';
import Button from 'components/App/Button';
import FananceDataTableInvoiceDetail from
'pages/Location/LocationDetail/Finance/FananceDataTableInvoiceDetail';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import images from 'assets/images';

import cn from 'utils/classnames';
import { convertIsoDateToString } from 'utils';
import { currencyFormat } from 'utils/currencyFormat';
import {
  CURRENCY_LABEL,
  themeColor,
  storageKey,
  LOGIN_TYPE,
} from 'constants/index';
import invoiceApi from 'api/invoice';

import styles from './FinanceDetail.module.scss';

function FinanceDetail({
  invoiceDetail,
  invoiceDetailLoading,
  getInvoiceDetail,
}) {
  const { id } = useParams();
  const { search } = useLocation();

  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(search);
    return {
      place: params.get('place'),
    };
  }, [search]);

  const placeId = searchQuery.place || invoiceDetail?.place || '';

  const isIntranet = React.useMemo(() => {
    const loginType = localStorage.getItem(storageKey.LOGIN_TYPE);
    return loginType === LOGIN_TYPE.INTRANET;
  }, []);

  React.useEffect(() => {
    if (id) {
      getInvoiceDetail({ id, place: placeId });
    }
  }, [id, placeId, getInvoiceDetail]);

  const [paying, setPaying] = React.useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = React.useState(false);

  const toggleConfirmModal = React.useCallback(() => {
    setIsOpenConfirmModal((prev) => !prev);
  }, []);

  const isPaid = React.useMemo(
    () =>
      invoiceDetail?.isPaid ||
      invoiceDetail?.userBookings?.some((booking) => booking.isPaid) ||
      false,
    [invoiceDetail],
  );

  const handlePayInvoice = React.useCallback(async () => {
    try {
      setPaying(true);
      await invoiceApi.payInvoice({ id, placeId });
      toast.success('Cập nhật trạng thái thành công');
      getInvoiceDetail({ id, place: placeId });
      setIsOpenConfirmModal(false);
    } catch (error) {
      if (error.response?.data?.key === 'INVOICE_ALREADY_PAID') {
        toast.error('Hóa đơn đã được thanh toán');
      } else {
        toast.error('Có lỗi xảy ra, vui lòng thử lại');
      }
    } finally {
      setPaying(false);
    }
  }, [id, placeId, getInvoiceDetail]);

  const breadcrumbList = React.useMemo(() => [
    {
      name: 'Tài chính',
      isActive: false,
      link: `/locations/detail/finance?place=${placeId}`,
    },
    {
      name: 'Chi tiết hoá đơn',
      isActive: true,
      link: '',
    },
  ], [placeId]);

  if (invoiceDetailLoading) {
    return <AppLoading loading={invoiceDetailLoading} loaderOnPage />;
  }

  return (
    <div className={cn('page-content', styles.Page)}>
      <Breadcrumb
        pageTitle="Chi tiết hoá đơn"
        breadcrumbList={breadcrumbList}
        className={styles.BreadcrumbContainer}
      />

      <Card className={`mb-4 ${styles.Card}`}>
        <div className={styles.InfoGrid}>
          <div className={styles.InfoCol}>
            <span className={styles.Label}>Ngày thanh toán</span>
            <h3 className={styles.Value}>
              {convertIsoDateToString(invoiceDetail?.createdAt)}
            </h3>
          </div>

          <div className={styles.InfoCol}>
            <span className={styles.Label}>
              {isPaid ? 'Đã thanh toán' : 'Chưa thanh toán'}
            </span>
            <h3 className={styles.Value}>
              {currencyFormat({
                currency: CURRENCY_LABEL,
                value: invoiceDetail?.totalAmount || 0,
              })}
            </h3>
          </div>

          <div className={styles.InfoCol}>
            <span className={styles.Label}>Số đơn đặt phòng</span>
            <h3 className={styles.Value}>
              {invoiceDetail?.userBookingCount || 0}
            </h3>
          </div>

          {isIntranet && (
            <div className={styles.InfoCol}>
              <div className={styles.SelectWrapper}>
                <Button
                  disabled={isPaid}
                  loading={paying}
                  onClick={toggleConfirmModal}
                  color={isPaid ? themeColor.SUCCESS : themeColor.PRIMARY}
                  className="text-button"
                >
                  {isPaid ? 'Đã thanh toán' : 'Xác nhận thanh toán'}
                </Button>

                <ModalRequestAction
                  isOpen={isOpenConfirmModal}
                  icon={images.modalWarning.default}
                  textRequest={'Bạn có chắc chắn muốn xác nhận \n thanh toán cho hoá đơn này không?'}
                  onToggle={toggleConfirmModal}
                  onCloseModal={toggleConfirmModal}
                  onConfirm={handlePayInvoice}
                  isLoadingConfirm={paying}
                />
              </div>
            </div>
          )}

        </div>
      </Card>

      <Card className="mb-4">
        <div className={styles.HeaderContainer}>
          <div className={`mb-3 ${styles.HeaderTop}`}>
            <h2 className={styles.HeaderTitle}>Danh sách đặt phòng</h2>
            <p className={styles.HeaderDescription}>
              Chi tiết những đơn đặt phòng được thanh toán trong hoá đơn
            </p>
          </div>
        </div>

        <FananceDataTableInvoiceDetail
          bookings={invoiceDetail?.userBookings || []}
        />
      </Card>
    </div>
  );
}

FinanceDetail.propTypes = {
  invoiceDetail: PropTypes.shape({
    _id: PropTypes.string,
    invoiceCode: PropTypes.string,
    place: PropTypes.string,
    totalAmount: PropTypes.number,
    createdAt: PropTypes.string,
    isPaid: PropTypes.bool,
    userBookingCount: PropTypes.number,
    userBookings: PropTypes.arrayOf(
      PropTypes.shape({
        booking: PropTypes.string,
        orderId: PropTypes.string,
        customerName: PropTypes.string,
        checkInDate: PropTypes.string,
        checkOutDate: PropTypes.string,
        amount: PropTypes.number,
        isPaid: PropTypes.bool,
      }),
    ),
  }),
  invoiceDetailLoading: PropTypes.bool.isRequired,
  getInvoiceDetail: PropTypes.func.isRequired,
};

FinanceDetail.defaultProps = {
  invoiceDetail: null,
};

export default FinanceDetail;
